import './styles/graphic.scss';
import mapboxgl from 'mapbox-gl';
import dayjs from 'dayjs';
import { nwsColors } from './data/nws-colors';


const determineWinter = () => {
  const currentMonth = new Date().getMonth();
  const winterMonths = [9, 10, 11, 0, 1, 2];
  if (winterMonths.includes(currentMonth)) {
    return true;
  }
  return false;
};

// selects the 7am forecast if it's winter, otherwise selects the 5pm forecast
const determineTemperatureLayer = (winter) => {
  const params = new URLSearchParams(document.location.search);
  const forecast = params.get('forecast');

  // if a forecast is provided as a param, use that forecast
  if (forecast && forecast === '7am') {
    return '7am';
  } if (forecast && forecast === '5pm') {
    return '5pm';
  } if (winter) {
    return '7am';
  }
  return '5pm';
};


const drawLegend = () => {
  const legendGradient = document.querySelector('.legend-gradient');
  const legendTicks = document.querySelector('.legend-ticks');
  const legendLabels = document.querySelector('.legend-labels');
  if (!legendGradient || !legendTicks || !legendLabels) return;

  legendGradient.innerHTML = '';
  legendTicks.innerHTML = '';
  legendLabels.innerHTML = '';

  // Determine seasonal temperature range based on current month
  const currentMonth = new Date().getMonth(); // 0 = January, 11 = December
  let minTemp;
  let maxTemp;
  let tickValues;

  if (currentMonth === 11 || currentMonth === 0 || currentMonth === 1) {
    // Winter: December, January, February
    minTemp = -20;
    maxTemp = 80;
    tickValues = [-20, 0, 20, 40, 60, 80];
  } else if (currentMonth >= 2 && currentMonth <= 4) {
    // Spring: March, April, May
    minTemp = 10;
    maxTemp = 100;
    tickValues = [10, 30, 50, 70, 90];
  } else if (currentMonth >= 5 && currentMonth <= 7) {
    // Summer: June, July, August
    minTemp = 40;
    maxTemp = 125;
    tickValues = [40, 60, 80, 100, 120];
  } else {
    // Fall: September, October, November
    minTemp = 10;
    maxTemp = 100;
    tickValues = [10, 30, 50, 70, 90];
  }

  const tempColors = nwsColors.filter(c => c[0] >= minTemp && c[0] <= maxTemp);

  tempColors.forEach((color) => {
    const block = document.createElement('div');
    block.className = 'legend-color-block';
    block.style.backgroundColor = `rgb(${color[1]}, ${color[2]}, ${color[3]})`;
    legendGradient.appendChild(block);
  });

  // Add tick marks and labels for specific temperature values
  tickValues.forEach((temp) => {
    const position = ((temp - minTemp) / (maxTemp - minTemp)) * 100;

    // Create tick mark
    const tick = document.createElement('div');
    tick.className = 'legend-tick';
    tick.style.left = `${position}%`;
    legendTicks.appendChild(tick);

    // Create label
    const label = document.createElement('li');
    label.textContent = temp;
    label.style.left = `${position}%`;
    legendLabels.appendChild(label);
  });
};

const drawMap = async (container, isMobile) => {
  // Fetches JSON data from a given URL
  const fetchData = async (url) => {
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (error) {
      // Logs an error if fetching data fails
      console.error('Error fetching data:', error);
      return null; // Return null on error
    }
  };

  const url = 'https://ix.cnn.io/data/apparent-temperature/city_forecasts.json';
  const forecastData = await fetchData(url);

  const winter = determineWinter();
  const temperatureLayer = determineTemperatureLayer(winter, forecastData);

  if (!forecastData || !forecastData.forecasts || forecastData.forecasts.length === 0) {
    console.error('Failed to load or parse forecast data. Not drawing map.');
    // The pym child may not be available, so we'll directly select the container if needed.
    const mapContainer = document.getElementById('map-content-wrapper');
    if (mapContainer) {
      mapContainer.innerHTML = '<p style="text-align: center; padding: 20px;">Could not load temperature forecast data at this time.</p>';
    }
    return;
  }

  // Sets the timestamp using the summary data
  if (forecastData.last_updated_str) {
    const sourceLink = forecastData.source_url ? `<a href="${forecastData.source_url}" target="_blank">${forecastData.source}</a>` : forecastData.source;
    const timestampString = `Notes: Data last checked on ${forecastData.last_updated_str}. Values reflect the forecasted "apparent temperature," which is the perceived temperature derived from either a combination of temperature and wind (wind chill) or temperature and humidity (heat index) for the indicated time. Colors are based on National Weather Service standards, with temperature ranges adjusted to enhance visual contrast.`;
    const timestampEl = document.getElementById('timestamp');
    if (timestampEl) {
      timestampEl.innerHTML = timestampString;
    }
  }

  // Set dek content dynamically based on season
  // All seasons now use 5 p.m. ET or 7 a.m. ET depending on winter/non-winter
  const currentMonth = new Date().getMonth();
  const isWinter = currentMonth === 11 || currentMonth === 0 || currentMonth === 1;

  const subhedDekElement = document.querySelector('.subhed.dek');
  if (subhedDekElement && (!subhedDekElement.textContent || subhedDekElement.textContent.trim() === '')) {
    const forecastTime = temperatureLayer === '7am' ? '7 a.m. Eastern Time' : '5 p.m. Eastern Time';
    subhedDekElement.textContent = `Here's the forecast for ${forecastTime} each day.`;
  }

  drawLegend();

  mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

  const optionsSm = {
    initZoom: 1.5,
    minZoom: 2,
    maxZoom: 7,
    center: [-97, 39],
    maxBounds: [[-126, 20], [-65, 55]],
  };

  const optionsLg = {
    initZoom: 3.1,
    minZoom: 3.25,
    maxZoom: 6,
    center: [-97, 38.5],
    maxBounds: [[-126, 20], [-65, 55]],
  };

  const options = isMobile ? optionsSm : optionsLg;

  const map = new mapboxgl.Map({
    container: container.node(),
    style: 'mapbox://styles/cnndigital/cmbfliit7009r01s1hvcoe2pb',
    center: options.center,
    zoom: options.initZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    maxBounds: options.maxBounds,
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  // map.dragPan.disable();

  if (!isMobile) {
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
    });
    map.addControl(nav, 'bottom-right');
  }

  let currentLayerId = null;
  let currentSourceId = null;

  const showErrorMessage = () => {
    const mapContainer = document.getElementById('map');
    if (!mapContainer) return;

    // Create overlay div
    const overlay = document.createElement('div');
    overlay.id = 'map-error-overlay';
    overlay.innerHTML = `
      <div class="error-message">
        <h3>Still processing latest forecast</h3>
        <p>Try again later.</p>
      </div>
    `;

    // Style the overlay
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(240, 240, 240, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      font-family: "CNN", "CNN Sans Display", "CNN Sans", "Helvetica Neue", Helvetica, Utkal, Arial, sans-serif;
    `;

    overlay.querySelector('.error-message').style.cssText = `
      text-align: center;
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    `;

    overlay.querySelector('h3').style.cssText = `
      margin: 0 0 10px 0;
      color: #262626;
      font-size: 18px;
      font-weight: 700;
    `;

    overlay.querySelector('p').style.cssText = `
      margin: 0;
      color: #595959;
      font-size: 14px;
    `;

    mapContainer.appendChild(overlay);
  };

  const hideErrorMessage = () => {
    const overlay = document.getElementById('map-error-overlay');
    if (overlay) {
      overlay.remove();
    }
  };

  const updateMapLayer = (forecast, ix, beforeLayerId) => {
    if (!map) return;
    if (currentLayerId && map.getLayer(currentLayerId)) {
      map.removeLayer(currentLayerId);
    }
    if (currentSourceId && map.getSource(currentSourceId)) {
      map.removeSource(currentSourceId);
    }

    // const dayId = forecast.day; // e.g., 'day_1'
    currentSourceId = `apparent-temp-source-day-${ix}`;
    currentLayerId = `apparent-temp-layer-day-${ix}`;
    const tilesetId = forecast.mapbox_tileset_id; // Use the tileset ID directly from the forecast object

    map.addSource(currentSourceId, {
      type: 'raster',
      url: `mapbox://${tilesetId}`,
      tileSize: 512,
    });

    map.addLayer({
      id: currentLayerId,
      type: 'raster',
      source: currentSourceId,
      paint: {
        'raster-opacity': 1,
      },
    }, beforeLayerId);

    // Hide error message when layer loads successfully
    hideErrorMessage();
  };

  map.on('load', () => {
    let firstSymbolId;
    for (const layer of map.getStyle().layers) {
      if (layer.type === 'symbol') {
        firstSymbolId = layer.id;
        break;
      }
    }

    map.addSource('us-states', {
      type: 'geojson',
      data: 'https://ix.cnn.io/data/gis/usa_states_conus.geojson',
    });

    const borderLayerId = 'state-borders-override';
    map.addLayer({
      id: borderLayerId,
      type: 'line',
      source: 'us-states',
      paint: {
        'line-color': '#ffffff',
        'line-width': 0.5,
      },
    }, firstSymbolId);

    const rasterBeforeLayerId = borderLayerId;

    const controlsContainer = document.getElementById('forecast-day-controls');
    if (controlsContainer) {
      controlsContainer.innerHTML = ''; // Clear previous controls
      forecastData.availableForecasts[temperatureLayer].forEach((forecast, index) => {
        const button = document.createElement('button');
        const date = new Date(forecast.time);
        const buttonText = dayjs(date).format('dddd');
        button.textContent = buttonText;
        // button.dataset.day = forecast.day;
        button.className = 'btn-day-selector';

        button.addEventListener('click', () => {
          updateMapLayer(forecast, index, rasterBeforeLayerId);

          controlsContainer.querySelectorAll('button').forEach(btn => btn.classList.remove('active'));
          button.classList.add('active');
        });

        controlsContainer.appendChild(button);

        if (index === 0) {
          button.classList.add('active');
        }
      });
    }

    const initialForecast = forecastData.availableForecasts[temperatureLayer][0];
    if (initialForecast) {
      updateMapLayer(initialForecast, 0, rasterBeforeLayerId);
    }

    window.pymChild && window.pymChild.sendHeight();
  });

  // Suppress 404 tile errors that don't affect functionality
  map.on('error', (e) => {
    if (e.error && e.error.message && e.error.message.includes('404')) {
      // Check if this is a tileset not found error (vs individual tile 404s)
      if (e.error.message.includes('.json')) {
        // This is a tileset metadata 404 - tileset doesn't exist
        showErrorMessage();
      }
      // Suppress all 404 errors from appearing in console
      return;
    }
    console.error('Map error:', e);
  });
};

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
  } = props;

  const mapWrapper = container.select('#map-content-wrapper');
  if (mapWrapper.empty()) {
    console.error('#map-content-wrapper not found within #graphic. Cannot render map.');
    return;
  }

  mapWrapper.html('');
  const mapDiv = mapWrapper.append('div')
    .attr('id', 'map')
    .style('width', `${initialWidth}px`)
    .style('height', `${initialHeight}px`);

  drawMap(mapDiv, isMobile);
}
